import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Typography } from "@mui/material"
import { DateTime } from "luxon"
import type { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { makeStyles } from "tss-react/mui"

import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import type { DtDateRange } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../../merchants/merchantHooks"

import { MerchantIdCardsHeader } from "../../components/MerchantIdCardsHeader"
import { useExportIdCardsList } from "../hooks/useExportIdCardsList"
import { fetchIdCardsList, MerchantIdCardsListQuery } from "../idCardsListApi"
import type { FetchIdCardsListResponse } from "../idCardsListApi"
import { DEFAULT_ID_CARDS_LIST_FILTERS, ID_CARD_TYPE_L10N_BASE, toIdCardsListView } from "../idCardsListHelpers"
import { IdCardStatus, IdCardStatusFilterOption } from "../idCardsListTypes"
import type { IdCardRow, IdCardsListFilters as Filters, IdCardsListView } from "../idCardsListTypes"
import { IdCardNumberCell } from "./IdCardNumberCell"
import { IdCardsListFilters } from "./IdCardsListFilters"
import { IdCardStatusChip } from "./IdCardStatusChip"

const useStyles = makeStyles()((theme) => ({
    inactiveRow: {
        "& td": {
            opacity: 0.7,
        },
    },
    actions: {
        paddingTop: "2.25rem",
        "& #mrt-search-button": {
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: ".75rem",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.common.white,
        },
    },
}))

function IdCardsListSummary({ view }: Readonly<{ view: IdCardsListView }>) {
    if (view.isFiltering) {
        return (
            <LocalizedStrict
                id="merchant-id-cards-list-summary-filtered"
                vars={{ shown: view.rows.length, total: view.total }}
                elems={{ bold: <b /> }}
            >
                <Typography variant="body2" color="textSecondary">
                    Showing <b>{view.rows.length}</b> of {view.total} card numbers
                </Typography>
            </LocalizedStrict>
        )
    }

    return (
        <LocalizedStrict
            id="merchant-id-cards-list-summary"
            vars={{ assigned: view.assignedCount, unassigned: view.unassignedCount }}
            elems={{ bold: <b /> }}
        >
            <Typography variant="body2" color="textSecondary">
                <b>{view.assignedCount}</b> assigned · {view.unassignedCount} unassigned
            </Typography>
        </LocalizedStrict>
    )
}

export function IdCardsList() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const tableInstanceRef = useRef<MRT_TableInstance<IdCardRow> | null>(null)

    const [filters, setFilters] = useState<Filters>(DEFAULT_ID_CARDS_LIST_FILTERS)
    const [search, setSearch] = useState<string>()
    const today = DateTime.now().startOf("day")
    const initialPeriod = useInitialDateFilters(today.minus({ years: 1 }), today)
    const [period, setPeriod] = useState<DtDateRange>(initialPeriod)

    const from = period.startDate.toISODate()
    const to = period.endDate.toISODate()

    const selectView = useCallback(
        (data: FetchIdCardsListResponse) =>
            toIdCardsListView({
                cards: data.idCards,
                filters,
                search,
                guestCardsLabel: l10n.getString("merchant-id-cards-list-filter-cardholder-guest"),
            }),
        [filters, search, l10n],
    )

    const idCardsQuery = useApiQuery(fetchIdCardsList, {
        queryName: MerchantIdCardsListQuery.ID_CARDS_LIST,
        dependencies: { merchantId, from, to },
        select: selectView,
    })
    const view = idCardsQuery.data

    const { ApiQueryTable, columnFactory } = useApiQueryTable<IdCardRow>({
        data: view?.rows,
        isInitialLoading: idCardsQuery.isInitialLoading,
        isFetching: idCardsQuery.isFetching,
        isError: idCardsQuery.isError,
    })

    // ApiQueryTable keeps the page when its data changes, which would leave the user on an empty page
    const filtersKey = JSON.stringify({ filters, search, from, to })
    const appliedFiltersKey = useRef(filtersKey)
    useEffect(() => {
        if (appliedFiltersKey.current === filtersKey) return
        appliedFiltersKey.current = filtersKey
        tableInstanceRef.current?.setPageIndex(0)
    }, [filtersKey])

    const toggleStatusFilter = useCallback((status: IdCardStatus) => {
        setFilters((prev) => ({
            ...prev,
            status: prev.status === status ? IdCardStatusFilterOption.ALL : status,
        }))
    }, [])

    const handleClearFilters = () => {
        setFilters(DEFAULT_ID_CARDS_LIST_FILTERS)
        setSearch(undefined)
    }

    const columns = useMemo<MRT_ColumnDef<IdCardRow>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-card-number"),
                accessorKey: "cardNumber",
                size: COLUMN_SIZE.XL,
                Cell: ({ row }) => <IdCardNumberCell card={row.original} />,
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-status"),
                accessorKey: "status",
                size: COLUMN_SIZE.SM,
                Cell: ({ row }) => <IdCardStatusChip status={row.original.status} onClick={toggleStatusFilter} />,
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-id-cards-table-card-type"),
                accessorKey: "cardType",
                size: COLUMN_SIZE.SM,
                Cell: ({ row }) => (
                    <LocalizedEnum base={ID_CARD_TYPE_L10N_BASE} enumValue={row.original.cardType}>
                        <Typography variant="body2" component="span" fontWeight={600}>
                            {row.original.cardType}
                        </Typography>
                    </LocalizedEnum>
                ),
            }),
        ],
        [l10n, columnFactory, toggleStatusFilter],
    )

    const exportIdCards = useExportIdCardsList(period)

    return (
        <>
            <MerchantIdCardsHeader
                hideNavGroupCrumb
                subtitleElement={view ? <IdCardsListSummary view={view} /> : undefined}
            />

            <ApiQueryTable
                columns={columns}
                tableInstanceRef={tableInstanceRef}
                initialState={{ density: "spacious" }}
                enableRowActions={false}
                enableGrouping={false}
                enableColumnFilters={false}
                muiTableBodyRowProps={({ row }) => ({
                    className: row.original.status === IdCardStatus.INACTIVE ? classes.inactiveRow : undefined,
                })}
                // rows are already filtered in select
                manualFiltering
                state={{ globalFilter: search }}
                onGlobalFilterChange={setSearch}
                muiSearchTextFieldProps={{
                    placeholder: l10n.getString("merchant-id-cards-list-search-placeholder"),
                }}
                renderTopToolbarCustomActions={() => (
                    <IdCardsListFilters
                        view={view}
                        isLoading={idCardsQuery.isInitialLoading}
                        isError={idCardsQuery.isError}
                        filters={filters}
                        search={search}
                        period={period}
                        onFiltersChange={setFilters}
                        onPeriodChange={setPeriod}
                        onClear={handleClearFilters}
                    />
                )}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table} stackClassName={classes.actions}>
                        <StandardExportButton
                            bordered
                            disabled={!view?.rows.length}
                            onClick={() =>
                                exportIdCards(table.getPrePaginationRowModel().rows.map((row) => row.original))
                            }
                        />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
